// WAT-11357, revised by WAT-11931 and WAT-12076 — Recommendation analytics for the AI Product
// Recommender.
//
// Four typed emitters, one per event_name this file pushes:
//   page_view                  AC2 (modal only)
//   quiz_status                AC3 (start) / AC9 (complete)
//   quiz_question_status       AC4 (displayed) / AC8 (answered)
//   quiz_recommendation_status AC10
//
// The link clicks (AC1 / AC5 / AC6 / AC7 / AC11 / AC12) are NOT pushed from here. GTM already
// reports a link_click for every click it sees, reading linkPosition and linkDestination off the
// nearest ancestor that carries data-linkposition / data-linkdestination and naming the click
// from the control itself. A push on top of that reports one click twice, once with values
// and once without. The builders below fill those attributes instead of a payload.
//
// Every linkPosition is a function of the placement: the modal's values carry a `modal_`
// prefix, the in-page ones do not, and nothing else differs between the two shells. The
// prefix therefore lives in ONE builder (quizLinkPosition) that the other three compose.
// AC numbers throughout this file are WAT-11931's.
//
// The ticket authors every AC as a raw `dataLayer.push`. These go through the DDS
// service instead, which pushes the SAME object: a raw push here would skip the
// parent-window handling the Storyblok visual editor needs (the editor renders the
// page in an iframe, so an un-forwarded push lands on a dataLayer no GTM container
// is listening to), and it would skip the app's post-paint analytics queue. The
// payload literals below are therefore written exactly as the ticket specifies them.
//
// Deferral is deliberately NOT implemented here. A bare import of the analytics
// service resolves to lib/service/analytics-defer/dds-analytics-wrapper.ts (the
// exact-match webpack alias in next.config.js, mirrored by jest's moduleNameMapper),
// and that wrapper already routes fireDataLayerEvent through the app's shared
// post-paint FIFO queue (WAT-11459, kill switch USE_ANALYTICS_PUSH_DEFER). Adding a
// second defer here would double-schedule these events and break the single-queue
// ordering guarantee the rest of the app depends on — including this component's own
// AC7-before-AC8 and AC9-before-AC10 requirements.

import { fireDataLayerEvent, getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";

/**
 * The feature's tool_id — dim_value for both quiz_status events (AC3/AC9), and the root
 * every linkPosition below is built on.
 */
const QUIZ_NAME = "product recommendation quiz";
/** Appended to the host page URL to name the quiz's own virtual page. Modal only. */
const QUIZ_VIRTUAL_PATH = "product_recommendation_quiz";
/** AC10 stand-in for a card whose package carries no shop slug / queryparam — see below. */
const UNKNOWN_ITEM_ID = "unknown";
/** What the modal's linkPosition values carry and the in-page ones do not. */
const MODAL_PREFIX = "modal_";

/**
 * AC1 — the entry CTA's position, and the root every other position here composes from.
 * Editing QUIZ_NAME therefore moves all of them AND quiz_status's dim_value together; split
 * the constant in two if those ever need to move apart.
 */
export const quizLinkPosition = (isModal: boolean): string => `${isModal ? MODAL_PREFIX : ""}${QUIZ_NAME}`;

// Analytics text is lower case by contract (AC1/4/5/6/7/8/11). Whitespace is collapsed
// too: these strings are CMS labels, and a stray newline or double space would split
// what should be one dimension value into several rows in the report.
// URLs deliberately do NOT go through this — see absoluteCtaUrl.
const analyticsText = (value?: string | null): string => (value || "").replace(/\s+/g, " ").trim().toLowerCase();

// A CMS question authored with a blank (or whitespace-only) label is NAMED rather than
// dropped. validate-recipe treats an empty label as a warning, not an error, so `unusable`
// does not hide such a question and it really can reach these builders.
//
// Naming beats omitting on both counts. Omitting left a dangling "2 of 4 - " / "… quiz - ",
// which reads as a real but empty dimension level; and collapsing the segment entirely made
// navLinkPosition(isModal, "") return exactly quizLinkPosition(isModal), so AC7's
// Next/Back/Close would have merged into AC1's entry-CTA row in any report grouped on
// linkPosition. Still true after WAT-11931 renamed both values.
// Exported so the spec pins the reported value by interpolating this constant rather than
// re-typing the literal, which would let the two drift apart silently.
export const UNTITLED_QUESTION = "(untitled)";
const questionText = (label?: string): string => analyticsText(label) || UNTITLED_QUESTION;

/**
 * `[question number of available questions] - [question text, lower case]` — AC4/AC8 dim_value.
 *
 * The number token is the WHOLE "2 of 4" progress string, not a bare ordinal (Jay,
 * 2026-08-06). Both halves are meaningful: the position is among VISIBLE questions, and the
 * total moves with them, so a run that skipped the conditional live-sports question reports
 * "1 of 3" where a run that took it reports "1 of 4". Callers pass the same string the step
 * indicator renders, so the report can never disagree with what the shopper saw.
 *
 * Only the LABEL gets the blank-value floor above; `progressLabel` deliberately does not. It is
 * built as `${cursor + 1} of ${visible.length}` at the single call site, so it is never blank,
 * and inventing a placeholder for it would name a state that cannot occur.
 */
export const questionDimValue = (progressLabel: string, label?: string): string => `${analyticsText(progressLabel)} - ${questionText(label)}`;

/**
 * AC5/AC6 — `[modal_]product recommendation quiz - [question number of available questions]
 * - [question text, lower case]`.
 */
export const optionLinkPosition = (isModal: boolean, progressLabel: string, label?: string): string => `${quizLinkPosition(isModal)} - ${questionDimValue(progressLabel, label)}`;

/**
 * AC7 — `[modal_]product recommendation quiz - [question text, lower case]`, deliberately
 * WITHOUT the progress token. The asymmetry against AC5/AC6 is the ticket's own wording in
 * both placements, so it is kept literal; if the two are meant to agree, AC7 is the one to
 * change.
 */
export const navLinkPosition = (isModal: boolean, label?: string): string => `${quizLinkPosition(isModal)} - ${questionText(label)}`;

/**
 * AC11/AC12 — `[modal_]product recommendation quiz recommendation`. Three controls share it:
 * the Shop CTA (AC11), the in-page-only "see all packages" (AC12) and Start over. Only the
 * first two have an AC; Start over inherits the rename by composition.
 */
export const recommendationLinkPosition = (isModal: boolean): string => `${quizLinkPosition(isModal)} recommendation`;

/**
 * The quiz's virtual page URL — the host page followed by /product_recommendation_quiz.
 * Modal only: AC2 is a modal event, and AC1 reports this for the modal but the host page URL
 * for in-page. The query string is dropped on purpose: it belongs to the host page
 * (utm_*, pzn, router-version), and keeping it would build the nonsense
 * "…/plans?utm_source=x/product_recommendation_quiz". Any trailing slash on the
 * pathname is collapsed so the homepage yields one separator, not two.
 */
export const quizVirtualUrl = (): string => {
  if (typeof window === "undefined") {
    return "";
  }
  const { origin, pathname } = window.location;
  return `${origin}${(pathname || "").replace(/\/+$/, "")}/${QUIZ_VIRTUAL_PATH}`;
};

/** AC5/AC6/AC7, and in-page AC1 — `[current page URL]`, verbatim. */
export const currentPageUrl = (): string => (typeof window === "undefined" ? "" : window.location.href);

/**
 * AC11 wants an absolute CCP URL. The builders return whatever the href needs — a relative
 * `/shop/...` path when derived, the authored string verbatim when a CMS row exists (so it can be
 * absolute, or protocol-relative on our own host) — so the origin is prepended only when it is
 * missing.
 *
 * Reporting only — both callers feed linkClickAttrs, while the `<a href>` is built from the raw
 * authored value, so what this returns never decides where the shopper lands. `new URL`
 * lower-cases the scheme and host. It also normalizes the path, absolute or not —
 * measured 2026-08-11: `/shop/a b/x` -> `/shop/a%20b/x`, `/shop/build/../dtvi/x` ->
 * `/shop/dtvi/x`. So an authored URL with a space or a `..` is reported normalized while the
 * `<a href>` carries it raw. Accepted: this value has to be resolvable, and the two agree for all
 * 12 authored URLs today.
 *
 * `new URL` rather than string concatenation because a hand-rolled scheme test mis-handles a
 * protocol-relative `//host/path`, resolving it against our origin and reporting the wrong place.
 */
export const absoluteCtaUrl = (url?: string | null): string => {
  if (!url) {
    return "";
  }
  if (typeof window === "undefined") {
    return url;
  }
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    // Not resolvable (a malformed CMS value): report it verbatim rather than dropping the
    // event — a wrong-looking destination is easier to trace than a missing one.
    return url;
  }
};

/**
 * AC10 item id for a broad (Signature) card. The ticket's example — `choice-package` —
 * is the last segment of buildBroadCtaUrl's path, so it is rebuilt from the same slug
 * rather than re-parsed out of the URL.
 */
export const broadItemId = (slug?: string | null): string => (slug ? `${analyticsText(slug)}-package` : "");

/**
 * AC10 item id for a focused (genre pack) card. The ticket does not define one — a genre
 * pack has no shop slug — so it reports the packages-config `queryparam` code, i.e. the same
 * token buildFocusedCtaUrl puts in preConfigItems, lower-cased to match the casing every
 * other analytics value in this story uses. Confirmed by Jay 2026-08-06 (Q4), whose
 * requirement is simply "the package names that are displayed".
 */
export const focusedItemId = (shopCodes: (string | null | undefined)[]): string => (shopCodes || []).map(code => analyticsText(code)).filter(Boolean).join("|");

// Every emitter is a no-op on the server. fireDataLayerEvent dereferences `window`
// unguarded, and the webpack alias that would otherwise route this through the defer
// queue is client-bundles-only — so on the server this import IS the raw DDS function
// and an unguarded call would throw during SSR.
const push = (payload: Record<string, unknown>): void => {
  if (typeof window === "undefined") {
    return;
  }
  fireDataLayerEvent(payload);
};

/**
 * What GTM reads off a control, or off the nearest ancestor of whatever node was clicked.
 * `aria-label` is in here because that is where GTM sources linkName from — a data-linkname
 * attribute is ignored.
 */
export type LinkClickAttrs = {
  "aria-label": string;
  "data-linkposition": string;
  "data-linkdestination": string;
};

/**
 * AC1 / AC5 / AC6 / AC7 / AC11 / AC12 — every reported control goes through the DDS helper, which
 * is the app-wide way to tag a link: it lowers the name, the position and the destination, and
 * puts the name on `aria-label` because that is the only place GTM reads it from.
 *
 * ONE CONTROL PER CALL. The name is the control's own label, so a pair shared by several controls
 * would report and announce them all under one name.
 *
 * The helper returns nothing at all unless both a name and a destination are present, which is
 * what keeps a first paint (no window-derived destination yet) from emitting a half pair. That
 * same gate is why the name gets UNTITLED_QUESTION as a floor: a blank CMS label would otherwise
 * take this control's position and destination down with it, and validate-recipe only warns.
 *
 * Setting `aria-label` REPLACES the accessible name a control would get from its own text. That
 * is accepted here: every caller passes the text it renders, so the change is one of case only.
 *
 * Do NOT also pass `googleanalyticsdata` to a Fusion Button here: its own onClick fires
 * fireCtaClickEventGA4 with it (fusion.atoms.button/dist/button.js), which is the second event
 * WAT-12076 removed.
 */
export const linkClickAttrs = (
  linkName: string,
  linkPosition: string,
  linkDestination: string
): Partial<LinkClickAttrs> => getAnalyticsAttrs(
  analyticsText(linkName) || UNTITLED_QUESTION,
  linkPosition,
  linkDestination
);

/**
 * AC2 — the only event here that is MODAL ONLY (the in-page card expands in place, so there
 * is no new context to mark), and the only one whose `event` is not `trackEvent`. No
 * placement parameter because there is no in-page caller; the cost is that a wrongly-added
 * one would be indistinguishable from real modal traffic, so the caller's gate is the only
 * defence.
 */
export const fireQuizPageView = (linkDestination: string): void => push({
  event: "page_view",
  event_name: "page_view",
  linkPosition: quizLinkPosition(true),
  linkDestination
});

/** AC3 (`start`) / AC9 (`complete`). */
export const fireQuizStatus = (dimName: "start" | "complete"): void => push({
  event: "trackEvent",
  event_name: "quiz_status",
  dim_name: dimName,
  dim_value: QUIZ_NAME
});

/** AC4 (`displayed`) / AC8 (`answered`). */
export const fireQuizQuestionStatus = (
  dimName: "displayed" | "answered",
  progressLabel: string,
  label?: string
): void => push({
  event: "trackEvent",
  event_name: "quiz_question_status",
  dim_name: dimName,
  dim_value: questionDimValue(progressLabel, label)
});

/**
 * AC10 — one id per displayed card, in display order, pipe-delimited.
 *
 * A card whose package has no shop slug / no packages-config `queryparam` reports
 * UNKNOWN_ITEM_ID rather than being dropped: both fields are optional in the adapter
 * (a pack missing `queryparam` is only warned about, not excluded), so dropping would make a
 * two-card screen indistinguishable from a genuine one-card recommendation. Keeping the slot
 * preserves the count, and "unknown" is greppable when a packages-config entry is wrong.
 *
 * The rule is applied uniformly: a screen where NOTHING resolves still reports, as
 * "unknown|unknown". Suppressing that case instead would leave AC9 `complete` without the
 * AC10 the ticket pairs it with, and would hide the very authoring bug the placeholder makes
 * greppable. Only a genuinely empty card list is skipped, which the component cannot produce
 * (`resultsVisible` requires a result) but the emitter should not assume.
 */
export const fireQuizRecommendationStatus = (itemIds: string[]): void => {
  const ids = itemIds || [];
  if (ids.length === 0) {
    return;
  }
  push({
    event: "trackEvent",
    event_name: "quiz_recommendation_status",
    dim_name: "displayed",
    dim_value: ids.map(id => id || UNKNOWN_ITEM_ID).join("|")
  });
};
